import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../hooks/useAuth';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  Cell, PieChart, Pie, Legend, CartesianGrid, ReferenceLine
} from 'recharts';
import {
  Users, Briefcase, CreditCard, DollarSign, Calendar,
  CheckCircle, Clock, Trash2, Edit2, Edit3, PlaySquare, AlertCircle, X,
  FileText, Search, Package, Repeat, TrendingUp, TrendingDown, Download, PieChart as PieChartIcon,
  Target, UserCheck, XCircle, Plus, ChevronDown, ChevronUp, Filter, ArrowUpRight, ArrowDownRight, Video, Link
} from 'lucide-react';
import api, {
  getSubscriptions, getInvoices, getExpenses, getMonthlyReport, getAITools,
  getCurrentRate, setExchangeRate, formatIQD, formatUSD, createExpense, updateExpense, getExpenseCategories, updateInvoice,
  deleteExpense, recordPayment, createInvoice, getClients, getClient,
  createClient, getClientLTV, getOverdueInvoices, createSubscription, getAllPackages,
  generateSubInvoice, getProjects, createProject, updateProject,
  updateSubscription, deleteSubscription, cancelSubscription, refundCancelSubscription,
  cancelInvoice, reversePayment, getInvoice,
  updateProjectStatus, archiveProject, getProjectSummary,
  getInvoiceStatement, getDailySales, getDailyDeliveries,
  getUsers, createUser, updateUser, deleteUser,
  getDeliverables, createDeliverable, deleteDeliverable,
  deleteProject, deleteInvoice, getEmployees,
  generateTrackingToken
} from '../utils/api';
import { parseArabicToEnglish } from '../utils/formatters';
import toast from 'react-hot-toast';
import {
  EXCHANGE_RATE, S, iqd2usd, td, pill, btn, today, Dual, StatCard, FilterBtn, Modal, LabeledInput, DetailRow, EmptyRow, DateRangeFilter, VIDEO_TYPES, DeliveryModal, VT_LABEL, VT_FILTER_TABS, SearchableSelect
} from '../components/SharedUI';

export default function SubscriptionsPage() {
  const [subs, setSubs] = useState([]);
  const [clients, setClients] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [packages, setPackages] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    client_id: '', package_id: '',
    period_start: new Date().toISOString().split('T')[0],
    period_end: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    allow_overage: false, carryover_enabled: false, notes: '',
    referring_employee_id: '', commission_rate: '',
    custom_name: 'باقة مخصصة', custom_seconds: '', custom_price_iqd: '', validity_days: 30
  });
  const [editSub, setEditSub] = useState(null);
  const [editForm, setEditForm] = useState({ status: '', allow_overage: false, carryover_enabled: false, notes: '', period_end: '' });
  const [quickDeliverySub, setQuickDeliverySub] = useState(null);
  
  // Refund Modal State
  const [refundSub, setRefundSub] = useState(null);
  const [refundAmount, setRefundAmount] = useState('');
  const [refundReason, setRefundReason] = useState('');

  // Cancel & Pause confirmation modals
  const [cancelSub, setCancelSub] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [pauseSub, setPauseSub] = useState(null);

  const [showHistory, setShowHistory] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const displaySubs = useMemo(() => {
    let filtered = subs;
    if (statusFilter === 'active') {
      filtered = filtered.filter(s => ['active', 'pending_payment'].includes(s.status));
    } else if (statusFilter === 'paused') {
      filtered = filtered.filter(s => ['paused', 'suspended'].includes(s.status));
    } else if (statusFilter !== 'all') {
      filtered = filtered.filter(s => s.status === statusFilter);
    } else if (!showHistory) {
      const seenClients = new Set();
      filtered = filtered.filter(s => {
        const isActive = ['active', 'pending_payment', 'suspended', 'paused'].includes(s.status);
        if (isActive) { seenClients.add(s.client_id); return true; }
        if (seenClients.has(s.client_id)) return false;
        seenClients.add(s.client_id);
        return true;
      });
    }
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      filtered = filtered.filter(s =>
        s.client_name?.toLowerCase().includes(q) || s.package_name?.toLowerCase().includes(q)
      );
    }
    return filtered;
  }, [subs, showHistory, searchQuery, statusFilter]);

  const load = useCallback(() => getSubscriptions().then(setSubs), []);
  useEffect(() => { load(); }, [load]);


  useEffect(() => {
    if (showCreate) {
      getClients().then(setClients);
      getAllPackages().then(setPackages);
      getEmployees().then(setEmployees);
    }
  }, [showCreate]);

  const selectedPkg = packages.find(p => String(p.id) === String(form.package_id));

  function resetForm() {
    const ts = new Date().toISOString().split('T')[0];
    const p30 = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
    setForm({
      client_id: '', package_id: '', period_start: ts, period_end: p30,
      allow_overage: false, carryover_enabled: false, notes: '',
      referring_employee_id: '', commission_rate: '',
      custom_name: 'باقة مخصصة', custom_seconds: '', custom_price_iqd: '', validity_days: 30
    });
  }

  const onEmployeeSelect = (empId) => {
    const emp = employees.find(e => e.id === Number(empId));
    setForm(f => ({ ...f, referring_employee_id: empId, commission_rate: emp ? (emp.commission_rate || 0) : '' }));
  };

  async function handleEditSub(e) {
    e.preventDefault();
    try {
      await updateSubscription(editSub.id, editForm);
      toast.success('تم تحديث الاشتراك');
      setEditSub(null);
      load();
    } catch (err) { toast.error(err.response?.data?.error || 'فشل التحديث'); }
  }

  async function handleSuspend(s) {
    try {
      await updateSubscription(s.id, { status: 'paused', allow_overage: s.allow_overage, carryover_enabled: s.carryover_enabled, notes: s.notes });
      toast.success('تم إيقاف الاشتراك');
      setPauseSub(null);
      load();
    } catch { toast.error('فشل العملية'); }
  }

  async function handleCancel(s, reason) {
    try {
      await cancelSubscription(s.id, reason);
      toast.success('تم إلغاء الاشتراك');
      setCancelSub(null);
      setCancelReason('');
      load();
    } catch (err) { toast.error(err.response?.data?.error || 'خطأ في الإلغاء'); }
  }

  async function handleActivate(s) {
    try {
      await updateSubscription(s.id, { status: 'active', allow_overage: s.allow_overage, carryover_enabled: s.carryover_enabled, notes: s.notes });
      toast.success('تم تفعيل الاشتراك ✅');
      load();
    } catch { toast.error('فشل العملية'); }
  }

  async function handleDeleteSub(s) {
    if (!window.confirm('هل تريد حذف هذا الاشتراك؟ لا يمكن التراجع')) return;
    try {
      await deleteSubscription(s.id);
      toast.success('تم حذف الاشتراك');
      load();
    } catch (err) { toast.error(err.response?.data?.error || 'فشل الحذف'); }
  }

  async function handleExtendPeriod(s, days = 30) {
    try {
      const currentEnd = new Date(s.period_end);
      const newEnd = new Date(currentEnd.getTime() + days * 24 * 60 * 60 * 1000);
      const newEndStr = newEnd.toISOString().split('T')[0];
      await updateSubscription(s.id, {
        status: s.status, allow_overage: !!s.allow_overage,
        carryover_enabled: !!s.carryover_enabled, notes: s.notes || '',
        period_end: newEndStr
      });
      toast.success(`تم تمديد الاشتراك 30 يوم — ينتهي: ${newEndStr}`);
      load();
    } catch (err) { toast.error(err.response?.data?.error || 'فشل التمديد'); }
  }

  async function handleCreate(e, forceRenew = false) {
    if (e) e.preventDefault();
    if (!form.client_id || !form.package_id || !form.period_start || !form.period_end)
      return toast.error('يرجى تعبئة جميع الحقول المطلوبة');
    setSaving(true);
    try {
      const data = {
        ...form,
        client_id: Number(form.client_id),
      };
      if (form.package_id !== 'custom') {
        data.package_id = Number(form.package_id);
      }
      if (forceRenew) {
        data.force_renew = true;
      }
      const res = await createSubscription(data);
      toast.success(`تم إنشاء الاشتراك وإرسال الفاتورة تلقائياً — رقم الفاتورة: ${res.invoice_number}`);
      setShowCreate(false);
      resetForm();
      load();
    } catch (err) {
      if (err.response?.status === 409 && err.response.data.can_force_renew) {
        const { remaining_days, remaining_seconds, period_end: oldEnd } = err.response.data;
        const confirmed = window.confirm(
          `⚠️ العميل لديه اشتراك نشط:\n` +
          `— باقي ${remaining_days} يوم و ${remaining_seconds} ثانية\n` +
          `— ينتهي بتاريخ ${oldEnd}\n\n` +
          `هل تريد التجديد مع ترحيل المدة المتبقية (${remaining_days} يوم) إلى الاشتراك الجديد؟`
        );
        if (confirmed) {
          setSaving(true);
          await handleCreate(null, true);
          return;
        }
      } else if (err.response?.status === 409) {
        toast.error(err.response.data.error, { duration: 6000 });
      } else {
        toast.error(err.response?.data?.error || 'فشل إنشاء الاشتراك');
      }
    } finally {
      setSaving(false);
    }
  }

  const statusCfg = {
    active: { bg: '#D1FAE5', color: '#065F46', label: 'نشط' },
    paused: { bg: '#FEF3C7', color: '#92400E', label: 'موقوف' },
    expired: { bg: '#FEE2E2', color: '#991B1B', label: 'منتهي' },
    suspended: { bg: '#FEE2E2', color: '#7F1D1D', label: 'موقوف (فاتورة)' },
    cancelled: { bg: '#F1F5F9', color: 'var(--text-gray, #64748B)', label: 'ملغي' },
    pending_payment: { bg: '#FEF3C7', color: '#92400E', label: 'بانتظار الدفع' },
  };
  const invStatusColor = { unpaid: '#E74C3C', partial: '#E67E22', paid: '#17A589' };
  const invStatusLabel = { unpaid: 'غير مدفوع', partial: 'جزئي', paid: 'مدفوع' };

  const Tip = ({ children, label }) => (
    <div className="relative group/tip inline-block">
      {children}
      <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2.5 py-1.5 rounded-lg bg-gray-900 dark:bg-gray-700 text-white text-[11px] font-semibold whitespace-nowrap opacity-0 group-hover/tip:opacity-100 transition-opacity duration-200 pointer-events-none shadow-lg z-50" style={{ fontFamily: 'Tajawal, sans-serif' }}>
        {label}
      </span>
    </div>
  );

  const isExpiredPeriod = (s) => s.status === 'active' && new Date(s.period_end) < new Date();

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h1 className="dark:!text-gray-100" style={{ margin: 0, fontSize: 20, fontWeight: 800, color: 'var(--text-dark-h, #0F1E33)', display: 'flex', alignItems: 'center', gap: 8 }}>
          <Repeat size={20} color="#9B8EC4" /> الباقات الشهرية
          <span className="dark:!text-gray-400" style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-muted, #94A3B8)', marginRight: 4 }}>({displaySubs.length})</span>
        </h1>
        <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
          <label className="dark:!text-gray-400" style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer', color: '#64748B', fontWeight: 600 }}>
            <input type="checkbox" checked={showHistory} onChange={e => setShowHistory(e.target.checked)} style={{ accentColor: '#4F46E5', width: 16, height: 16 }} />
            عرض السجل القديم
          </label>
          <button onClick={() => setShowCreate(true)} style={btn()}>
            <Plus size={15} /> اشتراك جديد
          </button>
        </div>
      </div>

      {/* Search & Filter Bar */}
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="relative flex-1 min-w-[200px] max-w-[320px]">
          <Search size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
          <input
            type="text"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder="بحث بالاسم أو الباقة..."
            className="w-full pr-10 pl-4 py-2.5 rounded-xl bg-white dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 text-sm text-gray-800 dark:text-gray-200 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/30 transition-all"
            style={{ fontFamily: 'Tajawal, sans-serif' }}
          />
        </div>
        <div className="flex bg-gray-100 dark:bg-gray-800/60 p-1 rounded-xl border border-gray-200/60 dark:border-gray-700/50">
          {[
            { key: 'all', label: 'الكل' },
            { key: 'active', label: 'نشط' },
            { key: 'paused', label: 'موقوف' },
            { key: 'expired', label: 'منتهي' },
            { key: 'cancelled', label: 'ملغي' },
          ].map(f => (
            <button
              key={f.key}
              onClick={() => setStatusFilter(f.key)}
              className={`px-3.5 py-1.5 text-xs font-bold rounded-lg transition-all ${
                statusFilter === f.key
                  ? 'bg-white dark:bg-gray-700 text-indigo-600 dark:text-indigo-400 shadow-sm'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
              style={{ fontFamily: 'Tajawal, sans-serif', border: 'none', cursor: 'pointer' }}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {showCreate && (
        <Modal onClose={() => { setShowCreate(false); resetForm(); }} title="إضافة اشتراك جديد" width={520}>
          <form onSubmit={handleCreate} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <LabeledInput label="العميل" required>
              <SearchableSelect
                value={form.client_id}
                onChange={val => setForm(f => ({ ...f, client_id: val }))}
                options={clients.map(c => ({ value: String(c.id), label: c.name }))}
                placeholder="— اختر العميل —"
                required
                style={S.input}
              />
            </LabeledInput>
            <LabeledInput label="الباقة" required>
              <select value={form.package_id} onChange={e => {
                const val = e.target.value;
                setForm(f => ({ ...f, package_id: val }));
              }} required style={S.input}>
                <option value="">— اختر الباقة —</option>
                {packages.map(p => <option key={p.id} value={p.id}>{p.name} — {formatIQD(p.price_iqd)}</option>)}
                <option value="custom" style={{ fontWeight: 'bold', color: '#4F46E5' }}>✨ باقة مخصصة (إدخال يدوي)</option>
              </select>
            </LabeledInput>

            {form.package_id === 'custom' && (
              <div className="dark:!bg-white/10 dark:!border-white/5 dark:!text-gray-100 dark:!shadow-none" style={{ background: 'var(--bg-flat-100, #F3F4F6)', borderRadius: 12, padding: 16, display: 'flex', flexDirection: 'column', gap: 10, border: '1px solid var(--border-light, #E2E8F0)' }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#4F46E5', marginBottom: 2 }}>تفاصيل الباقة المخصصة</div>
                <LabeledInput label="اسم الباقة (اختياري)">
                  <input value={form.custom_name} onChange={e => setForm(f => ({ ...f, custom_name: e.target.value }))} style={S.input} placeholder="مثال: باقة عرض خاص" />
                </LabeledInput>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <LabeledInput label="إجمالي الثواني">
                    <input type="text" inputMode="decimal" value={form.custom_seconds} onInput={(e) => { e.target.value = parseArabicToEnglish(e.target.value).replace(/[^0-9.]/g, ''); }} onChange={e => setForm(f => ({ ...f, custom_seconds: e.target.value }))} style={S.input} placeholder="مثال: 120" />
                  </LabeledInput>
                  <LabeledInput label="السعر (د.ع)">
                    <input type="text" inputMode="decimal" value={form.custom_price_iqd} onInput={(e) => { e.target.value = parseArabicToEnglish(e.target.value).replace(/[^0-9.]/g, ''); }} onChange={e => setForm(f => ({ ...f, custom_price_iqd: e.target.value }))} style={S.input} placeholder="مثال: 150000" />
                  </LabeledInput>
                </div>
                <LabeledInput label="صلاحية الباقة (بالأيام)">
                  <input type="text" inputMode="decimal" value={form.validity_days} onInput={(e) => { e.target.value = parseArabicToEnglish(e.target.value).replace(/[^0-9.]/g, ''); }} onChange={e => {
                    const days = parseInt(e.target.value) || 0;
                    const newEnd = new Date(new Date(form.period_start).getTime() + days * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
                    setForm(f => ({ ...f, validity_days: days, period_end: newEnd }));
                  }} style={S.input} placeholder="30" />
                </LabeledInput>
              </div>
            )}

            {selectedPkg && (
              <div style={{ background: '#F0ECF7', borderRadius: 8, padding: '10px 14px', fontSize: 12, color: '#1E40AF', display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                <span>ثواني: {selectedPkg.included_seconds}ث</span>
                <span>السعر: {formatIQD(selectedPkg.price_iqd)} / {formatUSD(selectedPkg.price_usd)}</span>
                <span>صلاحية: {selectedPkg.validity_days || 30} يوم</span>
              </div>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <LabeledInput label="بداية الفترة" required>
                <input type="date" value={form.period_start} onChange={e => {
                  const val = e.target.value;
                  const newEnd = new Date(new Date(val).getTime() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
                  setForm(f => ({ ...f, period_start: val, period_end: newEnd }));
                }} required style={S.input} />
              </LabeledInput>
              <LabeledInput label="نهاية الفترة (تلقائي 30 يوم)" required>
                <input type="date" value={form.period_end} disabled
                  className="dark:!bg-white/10 dark:!border-white/5 dark:!text-gray-100 dark:!shadow-none" style={{ ...S.input, background: 'var(--bg-flat-50, #F8FAFC)', color: 'var(--text-muted, #94A3B8)', cursor: 'not-allowed' }} />
              </LabeledInput>
            </div>
            <div style={{ display: 'flex', gap: 24 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 13, cursor: 'pointer' }}>
                <input type="checkbox" checked={form.allow_overage} onChange={e => setForm(f => ({ ...f, allow_overage: e.target.checked }))} />
                السماح بتجاوز الرصيد
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 13, cursor: 'pointer' }}>
                <input type="checkbox" checked={form.carryover_enabled} onChange={e => setForm(f => ({ ...f, carryover_enabled: e.target.checked }))} />
                ترحيل الرصيد
              </label>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <LabeledInput label="بواسطة (إحالة موظف)">
                <select value={form.referring_employee_id}
                  onChange={e => onEmployeeSelect(e.target.value)}
                  style={S.input}>
                  <option value="">— لا يوجد —</option>
                  {employees.filter(e => e.active || e.id === Number(form.referring_employee_id)).map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
                </select>
              </LabeledInput>
              <LabeledInput label="نسبة العمولة (%)">
                <input type="text" inputMode="decimal" value={form.commission_rate} onInput={(e) => { e.target.value = parseArabicToEnglish(e.target.value).replace(/[^0-9.]/g, ''); }}
                  onChange={e => setForm(f => ({ ...f, commission_rate: e.target.value }))}
                  style={S.input} placeholder="مثال: 10" />
              </LabeledInput>
            </div>

            <LabeledInput label="ملاحظات">
              <input type="text" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="اختياري" style={S.input} />
            </LabeledInput>
            <div style={{ background: '#FFFBEB', borderRadius: 8, padding: '10px 14px', fontSize: 12, color: '#92400E', display: 'flex', alignItems: 'center', gap: 8 }}>
              <FileText size={13} /> سيتم إنشاء فاتورة تلقائياً عند إضافة الاشتراك
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 4 }}>
              <button type="button" onClick={() => { setShowCreate(false); resetForm(); }} style={{ padding: '8px 18px', borderRadius: 8, border: '1px solid var(--border-light, #E2E8F0)', background: 'var(--bg-flat-white, white)', cursor: 'pointer', fontSize: 13, fontFamily: 'Tajawal, sans-serif' }}>إلغاء</button>
              <button type="submit" disabled={saving} style={{ ...btn(), opacity: saving ? .6 : 1 }}>
                {saving ? 'جاري الحفظ...' : 'إضافة الاشتراك'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 16 }}>
        {displaySubs.map(s => {
          const cfg = statusCfg[s.status] || statusCfg.expired;
          const invColor = invStatusColor[s.invoice_payment_status] || 'var(--text-muted, #94A3B8)';
          return (
            <div key={s.id}
              className="dark:!bg-gray-800/40 dark:!border-white/10 dark:!text-gray-100 dark:!shadow-none transition-all duration-300 hover:shadow-lg"
              style={{
                background: 'rgba(255, 255, 255, 0.7)',
                backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
                borderRadius: 16, padding: '20px 24px',
                border: '1px solid rgba(226, 232, 240, 0.7)',
                boxShadow: '0 4px 20px -2px rgba(0,0,0,0.03)'
              }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
                <div>
                  <div className="dark:!text-gray-100" style={{ fontWeight: 800, fontSize: 16, color: 'var(--text-dark-h, #0F1E33)', display: 'flex', alignItems: 'center', gap: 8 }}>
                    {s.client_name}
                    {s.client_tracking_token && (
                      <button
                        title="نسخ رابط التتبع للعميل"
                        onClick={(e) => {
                          e.preventDefault();
                          navigator.clipboard.writeText(`${window.location.origin}/track/${s.client_tracking_token}`);
                          toast.success('تم نسخ رابط التتبع');
                        }}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#8B5CF6', padding: '4px', display: 'flex', borderRadius: '50%', transition: 'background 0.2s' }}
                        onMouseOver={e => e.currentTarget.style.background = 'rgba(139, 92, 246, 0.1)'}
                        onMouseOut={e => e.currentTarget.style.background = 'transparent'}
                      >
                        <Link size={14} />
                      </button>
                    )}
                  </div>
                  <div className="dark:!text-gray-400" style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-muted, #64748B)', marginTop: 4 }}>{s.package_name}</div>
                </div>
                <span style={{ ...pill(cfg.bg, cfg.color), height: 'fit-content', fontWeight: 700, px: 10, py: 4, borderRadius: 20 }}>{cfg.label}</span>
              </div>
              {/* Suspended banner — above seconds bar */}
              {s.status === 'suspended' && (
                <div style={{
                  background: 'rgba(254, 242, 242, 0.8)', border: '1px solid rgba(254, 202, 202, 0.5)', borderRadius: 10,
                  padding: '10px 14px', marginBottom: 14, fontSize: 12,
                  color: '#B91C1C', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8
                }} className="dark:!bg-red-900/20 dark:!border-red-900/30 dark:!text-red-400">
                  <AlertCircle size={15} /> موقوف — يوجد فاتورة غير مسددة
                  {s.linked_invoice_number && (
                    <span style={{ opacity: 0.8, marginRight: 'auto' }}>({s.linked_invoice_number})</span>
                  )}
                </div>
              )}
              {/* Expired period warning — active but past end date */}
              {isExpiredPeriod(s) && (
                <div style={{
                  background: 'rgba(255, 237, 213, 0.8)', border: '1px solid rgba(253, 186, 116, 0.5)', borderRadius: 10,
                  padding: '10px 14px', marginBottom: 14, fontSize: 12,
                  color: '#9A3412', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8
                }} className="dark:!bg-orange-900/20 dark:!border-orange-900/30 dark:!text-orange-400">
                  <Calendar size={15} /> المدة منتهية — متبقي {s.remaining_seconds} ثانية
                  <button
                    onClick={() => handleExtendPeriod(s)}
                    className="hover:!bg-orange-500/20"
                    style={{ marginRight: 'auto', background: 'rgba(234, 88, 12, 0.1)', border: '1px solid rgba(234, 88, 12, 0.2)', borderRadius: 8, padding: '4px 12px', fontSize: 11, fontWeight: 700, color: '#EA580C', cursor: 'pointer', fontFamily: 'Tajawal, sans-serif', transition: 'all 0.2s' }}
                  >
                    تمديد 30 يوم
                  </button>
                </div>
              )}
              <div className="dark:!bg-gray-900/30 dark:!border-white/5 dark:!text-gray-100" style={{ background: 'rgba(248, 250, 252, 0.6)', borderRadius: 12, padding: '16px', marginBottom: 16, border: '1px solid rgba(226, 232, 240, 0.4)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
                  <span className="dark:!text-gray-400" style={{ fontSize: 12, color: 'var(--text-gray, #64748B)', display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600 }}>
                    <Clock size={13} /> محفظة الثواني
                  </span>
                  <span style={{ fontSize: 12, fontWeight: 800, color: s.usage_percent >= 80 ? '#EF4444' : s.usage_percent >= 50 ? '#F59E0B' : '#10B981' }}>
                    {s.usage_percent}%
                  </span>
                </div>
                <div className="dark:!bg-gray-700/50" style={{ height: 6, background: 'rgba(226, 232, 240, 0.6)', borderRadius: 6, overflow: 'hidden', marginBottom: 10 }}>
                  <div style={{
                    height: '100%', borderRadius: 6, transition: 'width .8s cubic-bezier(0.4, 0, 0.2, 1)',
                    width: `${Math.min(s.usage_percent, 100)}%`,
                    background: s.usage_percent >= 80 ? 'linear-gradient(90deg, #F87171, #EF4444)' : s.usage_percent >= 50 ? 'linear-gradient(90deg, #FBBF24, #F59E0B)' : 'linear-gradient(90deg, #34D399, #10B981)'
                  }} />
                </div>
                <div className="dark:!text-gray-400" style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--text-muted, #94A3B8)', fontWeight: 500 }}>
                  <span>مستخدم: {s.used_seconds}ث</span>
                  <span style={{ fontWeight: 700, color: s.usage_percent >= 80 ? '#EF4444' : 'inherit' }}>متبقي: {s.remaining_seconds}ث</span>
                  <span>الإجمالي: {s.total_seconds}ث</span>
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: s.linked_invoice_number ? 12 : 0, paddingLeft: 4, paddingRight: 4 }}>
                <span className="dark:!text-gray-400" style={{ fontSize: 12, color: 'var(--text-muted, #94A3B8)', fontWeight: 500 }}>{s.period_start} ← {s.period_end}</span>
                <div style={{ textAlign: 'left' }}>
                  <div className="dark:!text-indigo-300" style={{ fontWeight: 800, color: '#4F46E5', fontSize: 14 }}>{formatIQD(s.price_iqd)}</div>
                  <div className="dark:!text-gray-400" style={{ fontSize: 11, color: 'var(--text-muted, #94A3B8)', fontWeight: 500 }}>{formatUSD(s.price_usd)}</div>
                </div>
              </div>
              {s.linked_invoice_number && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px dashed rgba(226, 232, 240, 0.8)', paddingTop: 12, paddingLeft: 4, paddingRight: 4 }} className="dark:!border-gray-700/50">
                  <span className="dark:!text-gray-400" style={{ fontSize: 12, color: 'var(--text-gray, #64748B)', display: 'flex', alignItems: 'center', gap: 6, fontWeight: 500 }}>
                    <FileText size={13} /> {s.linked_invoice_number}
                  </span>
                  <span style={{ fontSize: 12, fontWeight: 700, color: invColor }}>
                    {invStatusLabel[s.invoice_payment_status] || '—'}
                  </span>
                </div>
              )}

              {/* Renewal button — soft primary style */}
              {(s.invoice_payment_status === 'paid' || !s.linked_invoice_number) && s.status === 'active' && (
                <button
                  onClick={async () => {
                    try {
                      const res = await generateSubInvoice(s.id);
                      toast.success(`تم إنشاء فاتورة التجديد — ${res.invoice_number}`);
                      getSubscriptions().then(setSubs);
                    } catch (err) {
                      toast.error(err.response?.data?.error || 'خطأ في إنشاء الفاتورة');
                    }
                  }}
                  className="dark:!bg-indigo-500/10 dark:!text-indigo-300 dark:hover:!bg-indigo-500/20"
                  style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    width: '100%', justifyContent: 'center', marginTop: 16,
                    fontSize: 13, fontWeight: 700,
                    borderRadius: 12, padding: '10px 0',
                    background: 'rgba(99, 102, 241, 0.08)',
                    color: '#4F46E5', border: '1px solid rgba(99, 102, 241, 0.1)', cursor: 'pointer',
                    transition: 'all 0.2s ease', fontFamily: 'Tajawal, sans-serif'
                  }}
                  onMouseOver={e => {
                    e.currentTarget.style.background = 'rgba(99, 102, 241, 0.12)';
                  }}
                  onMouseOut={e => {
                    e.currentTarget.style.background = 'rgba(99, 102, 241, 0.08)';
                  }}
                >
                  <Repeat size={16} /> إصدار فاتورة التجديد
                </button>
              )}

              {/* Quick delivery button — soft secondary style */}
              {s.status === 'active' && (
                <button
                  onClick={() => setQuickDeliverySub(s)}
                  className="dark:!bg-teal-500/10 dark:!text-teal-400 dark:hover:!bg-teal-500/20"
                  style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    width: '100%', justifyContent: 'center', marginTop: 10,
                    fontSize: 13, fontWeight: 700,
                    borderRadius: 12, padding: '10px 0',
                    background: 'rgba(20, 184, 166, 0.08)',
                    color: '#0D9488', border: '1px solid rgba(20, 184, 166, 0.1)', cursor: 'pointer',
                    transition: 'all 0.2s ease', fontFamily: 'Tajawal, sans-serif'
                  }}
                  onMouseOver={e => e.currentTarget.style.background = 'rgba(20, 184, 166, 0.15)'}
                  onMouseOut={e => e.currentTarget.style.background = 'rgba(20, 184, 166, 0.08)'}
                >
                  <Video size={16} /> تسجيل تسليم فيديو
                </button>
              )}

              {/* Action buttons with visible tooltips */}
              <div style={{ display: 'flex', gap: 6, marginTop: 16, borderTop: '1px dashed rgba(226, 232, 240, 0.8)', paddingTop: 16, justifyContent: 'center', flexWrap: 'wrap' }} className="dark:!border-gray-700/50">
                <Tip label="تعديل">
                  <button
                    onClick={() => { setEditSub(s); setEditForm({ status: s.status, allow_overage: !!s.allow_overage, carryover_enabled: !!s.carryover_enabled, notes: s.notes || '', period_end: s.period_end || '' }); }}
                    style={{ background: 'transparent', border: 'none', padding: '8px', borderRadius: '50%', color: '#94A3B8', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s' }}
                    onMouseOver={e => { e.currentTarget.style.background = 'rgba(0,0,0,0.04)'; e.currentTarget.style.color = '#334155'; }}
                    onMouseOut={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#94A3B8'; }}
                  ><Edit2 size={16} /></button>
                </Tip>
                {s.status === 'active' && (
                  <Tip label="إيقاف مؤقت">
                    <button onClick={() => setPauseSub(s)}
                      style={{ background: 'transparent', border: 'none', padding: '8px', borderRadius: '50%', color: '#94A3B8', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s' }}
                      onMouseOver={e => { e.currentTarget.style.background = 'rgba(245, 158, 11, 0.1)'; e.currentTarget.style.color = '#D97706'; }}
                      onMouseOut={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#94A3B8'; }}
                    ><Clock size={16} /></button>
                  </Tip>
                )}
                {(s.status === 'paused' || s.status === 'suspended') && (
                  <Tip label="تفعيل">
                    <button onClick={() => handleActivate(s)}
                      style={{ background: 'transparent', border: 'none', padding: '8px', borderRadius: '50%', color: '#94A3B8', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s' }}
                      onMouseOver={e => { e.currentTarget.style.background = 'rgba(16, 185, 129, 0.1)'; e.currentTarget.style.color = '#059669'; }}
                      onMouseOut={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#94A3B8'; }}
                    ><PlaySquare size={16} /></button>
                  </Tip>
                )}
                {s.status !== 'cancelled' && (
                  <Tip label="إلغاء">
                    <button
                      onClick={() => { setCancelReason(''); setCancelSub(s); }}
                      style={{ background: 'transparent', border: 'none', padding: '8px', borderRadius: '50%', color: '#94A3B8', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s' }}
                      onMouseOver={e => { e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)'; e.currentTarget.style.color = '#DC2626'; }}
                      onMouseOut={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#94A3B8'; }}
                    ><XCircle size={16} /></button>
                  </Tip>
                )}
                {s.status !== 'cancelled' && s.remaining_seconds > 0 && (
                  <Tip label="استرداد">
                    <button
                      onClick={() => {
                        const pricePerSec = s.price_iqd / s.total_seconds;
                        const suggestedRefund = Math.round(s.remaining_seconds * pricePerSec);
                        setRefundAmount(suggestedRefund.toString());
                        setRefundReason('');
                        setRefundSub(s);
                      }}
                      style={{ background: 'transparent', border: 'none', padding: '8px', borderRadius: '50%', color: '#94A3B8', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s' }}
                      onMouseOver={e => { e.currentTarget.style.background = 'rgba(234, 179, 8, 0.1)'; e.currentTarget.style.color = '#EAB308'; }}
                      onMouseOut={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#94A3B8'; }}
                    ><DollarSign size={16} /></button>
                  </Tip>
                )}
                <Tip label="حذف">
                  <button onClick={() => handleDeleteSub(s)}
                    style={{ background: 'transparent', border: 'none', padding: '8px', borderRadius: '50%', color: '#94A3B8', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s' }}
                    onMouseOver={e => { e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)'; e.currentTarget.style.color = '#DC2626'; }}
                    onMouseOut={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#94A3B8'; }}
                  ><Trash2 size={16} /></button>
                </Tip>
              </div>
            </div>
          );
        })}
        {displaySubs.length === 0 && (
          <div className="dark:!text-gray-400" style={{ gridColumn: '1/-1', textAlign: 'center', color: 'var(--text-muted, #94A3B8)', padding: 56, fontSize: 14 }}>
            لا توجد اشتراكات
          </div>
        )}
      </div>

      {/* Refund Modal */}
      {refundSub && (
        <Modal onClose={() => setRefundSub(null)} title="إلغاء مع استرداد" width={400}>
          <div className="flex flex-col gap-4 p-1">
            
            {/* Stats Grid */}
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-gray-50 dark:bg-dark-deeper rounded-xl p-3 border border-gray-100 dark:border-gray-800 flex flex-col justify-center items-center text-center">
                <span className="text-gray-500 dark:text-gray-400 text-xs mb-1 font-medium">متبقي للاسترداد</span>
                <span className="text-lg font-bold text-primary dark:text-primary-dark">
                  {refundSub.remaining_seconds} <span className="text-xs font-normal text-gray-400">ثانية</span>
                </span>
              </div>
              <div className="bg-gray-50 dark:bg-dark-deeper rounded-xl p-3 border border-gray-100 dark:border-gray-800 flex flex-col justify-center items-center text-center">
                <span className="text-gray-500 dark:text-gray-400 text-xs mb-1 font-medium">قيمة الثانية</span>
                <span className="text-lg font-bold text-slate-700 dark:text-slate-300">
                  {Math.round(refundSub.price_iqd / refundSub.total_seconds).toLocaleString()} <span className="text-xs font-normal text-gray-400">د.ع</span>
                </span>
              </div>
            </div>

            <div className="bg-blue-50/50 dark:bg-blue-900/10 border border-blue-100 dark:border-blue-900/30 rounded-lg p-2.5 px-3 text-sm flex justify-between items-center">
               <span className="text-gray-600 dark:text-gray-400 font-medium text-xs">معدل الاستهلاك ({Math.round(((refundSub.used_seconds || 0) * 100) / refundSub.total_seconds)}%)</span>
               <span className="font-semibold text-gray-800 dark:text-gray-200 text-xs">{refundSub.used_seconds || 0} / {refundSub.total_seconds} ثانية</span>
            </div>
            
            <div className="flex flex-col gap-3 mt-1">
              <LabeledInput label="مبلغ الاسترداد (د.ع)">
                <div className="relative">
                  <input
                    type="text" inputMode="decimal"
                    value={refundAmount}
                    onInput={(e) => { e.target.value = parseArabicToEnglish(e.target.value).replace(/[^0-9.]/g, ''); }}
                    onChange={e => setRefundAmount(e.target.value)}
                    className="w-full bg-white dark:bg-dark-deeper border border-gray-200 dark:border-gray-700 rounded-lg px-4 py-2.5 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all font-semibold"
                    min="0"
                    max={refundSub.price_iqd}
                  />
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs font-medium pointer-events-none">
                    دينار عراقي
                  </div>
                </div>
              </LabeledInput>
              
              <LabeledInput label="سبب الإلغاء (اختياري)">
                <input
                  type="text"
                  value={refundReason}
                  onChange={e => setRefundReason(e.target.value)}
                  className="w-full bg-white dark:bg-dark-deeper border border-gray-200 dark:border-gray-700 rounded-lg px-4 py-2.5 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all text-sm"
                  placeholder="مثال: بناء على طلب العميل..."
                />
              </LabeledInput>
            </div>

            <div className="flex gap-2 mt-3 pt-3 border-t border-gray-100 dark:border-gray-800">
              <button
                onClick={() => setRefundSub(null)}
                className="flex-1 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors font-medium text-sm"
              >
                تراجع
              </button>
              <button
                onClick={async () => {
                  const amt = parseInt(refundAmount);
                  if (isNaN(amt) || amt < 0) return toast.error('مبلغ غير صالح');
                  if (amt > refundSub.price_iqd) return toast.error('المبلغ أكبر من سعر الاشتراك!');
                  
                  try {
                    const result = await refundCancelSubscription(refundSub.id, refundReason, amt);
                    toast.success(`تم الاسترداد: ${result.refund_iqd?.toLocaleString()} د.ع`);
                    setRefundSub(null);
                    load();
                  } catch (err) {
                    toast.error(err.response?.data?.error || 'خطأ في الاسترداد');
                  }
                }}
                className="flex-[2] py-2.5 rounded-lg bg-danger hover:bg-red-600 text-white transition-colors font-medium text-sm shadow-sm shadow-danger/20"
              >
                تأكيد والإلغاء
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Quick delivery modal */}
      {quickDeliverySub && (
        <DeliveryModal
          prefilledSub={quickDeliverySub}
          onClose={() => setQuickDeliverySub(null)}
          onSuccess={() => { setQuickDeliverySub(null); load(); }}
        />
      )}

      {/* Edit subscription modal */}
      {editSub && (
        <Modal onClose={() => setEditSub(null)} title={'تعديل اشتراك — ' + editSub.client_name} width={480}>
          <form onSubmit={handleEditSub} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <LabeledInput label="الحالة">
              <select value={editForm.status} onChange={e => setEditForm(f => ({ ...f, status: e.target.value }))} style={S.input}>
                <option value="active">نشط</option>
                <option value="paused">موقوف</option>
                <option value="expired">منتهي</option>
              </select>
            </LabeledInput>
            <LabeledInput label="تاريخ نهاية الفترة">
              <div style={{ display: 'flex', gap: 8 }}>
                <input type="date" value={editForm.period_end} disabled
                  className="dark:!bg-white/10 dark:!border-white/5 dark:!text-gray-100 dark:!shadow-none" style={{ ...S.input, background: 'var(--bg-flat-50, #F8FAFC)', color: 'var(--text-gray, #64748B)', flex: 1, cursor: 'not-allowed' }} />
                <button type="button" onClick={() => {
                  const currentStr = editForm.period_end || editSub.period_end;
                  if (!currentStr) return;
                  const newEnd = new Date(new Date(currentStr).getTime() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
                  setEditForm(f => ({ ...f, period_end: newEnd }));
                }} style={{ ...btn('#F0ECF7', '#9B8EC4'), padding: '0 16px', flexShrink: 0 }}>
                  <Plus size={14} style={{ marginRight: -4 }} /> إضافة 30 يوم
                </button>
              </div>
            </LabeledInput>
            <div style={{ display: 'flex', gap: 24 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 13, cursor: 'pointer' }}>
                <input type="checkbox" checked={editForm.allow_overage} onChange={e => setEditForm(f => ({ ...f, allow_overage: e.target.checked }))} />
                السماح بتجاوز الرصيد
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 13, cursor: 'pointer' }}>
                <input type="checkbox" checked={editForm.carryover_enabled} onChange={e => setEditForm(f => ({ ...f, carryover_enabled: e.target.checked }))} />
                ترحيل الرصيد
              </label>
            </div>
            <LabeledInput label="ملاحظات">
              <input type="text" value={editForm.notes} onChange={e => setEditForm(f => ({ ...f, notes: e.target.value }))} style={S.input} />
            </LabeledInput>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
              <button type="button" onClick={() => setEditSub(null)} style={btn('#F1F5F9', 'var(--text-dark-s, #374151)')}>إلغاء</button>
              <button type="submit" style={btn()}>حفظ التعديلات</button>
            </div>
          </form>
        </Modal>
      )}

      {/* Pause Confirmation Modal */}
      {pauseSub && (
        <Modal onClose={() => setPauseSub(null)} title={'إيقاف اشتراك — ' + pauseSub.client_name} width={400}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div className="dark:!text-gray-300" style={{ fontSize: 14, color: '#374151', lineHeight: 1.8 }}>
              <AlertCircle size={16} style={{ display: 'inline', verticalAlign: 'middle', marginLeft: 6, color: '#F59E0B' }} />
              هل تريد إيقاف اشتراك <strong>{pauseSub.client_name}</strong> مؤقتاً؟
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, borderTop: '1px solid rgba(226,232,240,0.6)', paddingTop: 14 }} className="dark:!border-gray-700/50">
              <button onClick={() => setPauseSub(null)} style={btn('#F1F5F9', '#374151')}>تراجع</button>
              <button onClick={() => handleSuspend(pauseSub)} style={{ ...btn('#FEF3C7', '#92400E'), fontWeight: 700 }}>
                <Clock size={14} /> إيقاف مؤقت
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Cancel Confirmation Modal */}
      {cancelSub && (
        <Modal onClose={() => setCancelSub(null)} title={'إلغاء اشتراك — ' + cancelSub.client_name} width={420}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div className="dark:!text-gray-300" style={{ fontSize: 14, color: '#374151', lineHeight: 1.8 }}>
              <AlertCircle size={16} style={{ display: 'inline', verticalAlign: 'middle', marginLeft: 6, color: '#EF4444' }} />
              هل تريد إلغاء اشتراك <strong>{cancelSub.client_name}</strong>؟
            </div>
            <LabeledInput label="سبب الإلغاء (اختياري)">
              <input type="text" value={cancelReason} onChange={e => setCancelReason(e.target.value)} placeholder="مثال: بناء على طلب العميل..." style={S.input} />
            </LabeledInput>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, borderTop: '1px solid rgba(226,232,240,0.6)', paddingTop: 14 }} className="dark:!border-gray-700/50">
              <button onClick={() => setCancelSub(null)} style={btn('#F1F5F9', '#374151')}>تراجع</button>
              <button onClick={() => handleCancel(cancelSub, cancelReason)} style={{ ...btn('#FEE2E2', '#991B1B'), fontWeight: 700 }}>
                <XCircle size={14} /> تأكيد الإلغاء
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
